import { useEffect, useRef, useState, type ReactNode } from "react";
import { matchingEffectCategories } from "@/libs/games/haptic-kind-categories";
import type {
  HapticSummary,
  HapticSummaryKind,
} from "@/libs/games/haptic-summary.server";
import { analyticsInstance } from "@/libs/analytics";
import { DeviceType, SupportType } from "@/services/ContentsService";
import DeviceUtil from "@/utils/DeviceUtil";
import { useTrans } from "@/utils/useTrans";
import { Tooltip } from "@/v2-components/ui/tooltip";
import InfoIcon from "@/v2-components/core/icon/info-icon";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";

/**
 * 게임 상세 「AI 햅틱 요약」 카드(베타) — 「이 게임을 bHaptics 로 하면 무엇을 느끼나」 3~4줄.
 *
 * - 데이터는 `getServerSideProps` 가 그 게임 것만 내려준다(`getHapticSummary`). 없으면 호출처가 그리지 않는다.
 * - **AI 표시**: ✨ 단독 대신 「AI」 표시 + 무엇을 했는지 동사로(EU AI Act 제50조 가이드 해석 — ✨ 는 모호하다는 견해).
 *   EU 공식 AI 아이콘 에셋이 들어오면 「AI」 글자 상자를 교체한다.
 * - **숫자를 두지 않는다 — 칩도, AI 표시의 이벤트 수도.** 개수는 이펙트 섹션(어드민 Haptic Effects)이 맡는다.
 *   2026-10-26 부터 워크스페이스가 있는 게임 238개 전부에 어드민 이펙트가 채워져, 요약이 있는 게임엔 이펙트 섹션도 늘 있다.
 *   포털 이벤트 수와 이펙트 섹션 합계(오디오 패턴 수)는 세는 기준이 달라 한 페이지에 숫자가 두 벌이면 오류처럼 보인다
 *   (Into the Radius: 이펙트 Recoil 19 vs 포털 분류 반동 24).
 * - 줄 아이콘은 베타용 이모지다 — 디자인 시스템 아이콘이 오면 교체(`docs/pitfalls.md` #28 「아이콘 · SVG」: 손으로 근사 금지).
 * - **요약이 없는 게임은 안내 모드**(`summary` 없이 `support`) — 원료(포털 이벤트)가 없는 게임(prod 공개 413개 중 190개, 2026-10-26)도
 *   비워 두지 않는다. 지원 타입별 고정 문구 + 아래 기기 줄.
 *   How to Play 링크는 두지 않는다 — 같은 페이지에 「플레이 방법」 버튼이 이미 있다(데스크탑 aside `GameGuideButtons` · 모바일 히어로). AI 가 만든 게 아니라 AI 표시를 붙이지 않는다.
 *   문구는 확인된 사실만 — 「바로 된다」·「개발사가 넣었다」는 쓰지 않는다(정책 문서 §3).
 */
// 지원 타입별 안내 한 줄 — 사이트의 지원 타입 설명(`SUPPORT_TYPE_DESC`)과 같은 사실만
const SUPPORT_LINE: Record<SupportType, string> = {
  [SupportType.Native]: "Haptic support Native",
  [SupportType.Mod]: "Haptic support Mod",
  [SupportType.OfficialMod]: "Haptic support Official Mod",
  [SupportType.AiPowered]: "Haptic support AI Powered",
};

const KIND_STYLE: Record<HapticSummaryKind, { icon: string; color: string }> = {
  impact: { icon: "💥", color: "#e5484d" },
  action: { icon: "🎯", color: "#0f62fe" },
  movement: { icon: "🏃", color: "#12a594" },
  status: { icon: "❤️", color: "#d6409f" },
  world: { icon: "🌍", color: "#f76b15" },
  touch: { icon: "🤝", color: "#8e4ec6" },
};

export default function HapticSummaryCard({
  slug,
  gameName,
  summary,
  support,
  devices = [],
  hasEffects = false,
  effectCategories = [],
  onShowEffects,
}: {
  slug: string;
  /** 게임 이름 — 기기 줄의 제안 문구에 넣는다(「Counter-Strike 2, 이제 몸으로 즐기세요.」) */
  gameName: string;
  /** AI 요약 — 없으면 `support` 로 안내 모드 */
  summary?: HapticSummary | null;
  /** 안내 모드: 지원 타입 */
  support?: { type: SupportType };
  devices?: DeviceType[];
  /** 이펙트 섹션도 있는 게임인가 — 노출 이벤트(`has_effects`)에만 쓴다 */
  hasEffects?: boolean;
  /** 이 게임 어드민 이펙트 카테고리 — 줄과 맞는 카테고리가 있으면 그 줄을 누를 수 있다 */
  effectCategories?: string[];
  /** 줄을 누르면 이펙트 「전체 보기」 시트를 그 카테고리 강조로 연다(페이지가 시트 상태를 쥔다) */
  onShowEffects?: (categories: string[]) => void;
}) {
  const { t } = useTrans(["games"]);
  const cardType = summary ? "summary" : "support";
  const ref = useRef<HTMLElement>(null);
  const sent = useRef(false);
  // 모바일은 처음 2줄만 — 카드가 첫 화면을 다 차지했다(약 660px, 정책 문서 §5). 데스크탑은 늘 전부
  const [expanded, setExpanded] = useState(false);
  const MOBILE_LINES = 2;

  // 노출 = 카드가 절반 이상 보였을 때 1회(같은 라우트에서 slug 가 바뀌면 다시 센다)
  useEffect(() => {
    sent.current = false;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !sent.current) {
          sent.current = true;
          analyticsInstance?.logHapticSummaryView({
            gameSlug: slug,
            hasEffects,
            cardType,
          });
          io.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [slug, hasEffects, cardType]);

  const kindLabel: Record<HapticSummaryKind, string> = {
    impact: t("Haptic kind Impact"),
    action: t("Haptic kind Action"),
    movement: t("Haptic kind Movement"),
    status: t("Haptic kind Status"),
    world: t("Haptic kind World"),
    touch: t("Haptic kind Touch"),
  };
  // 기기마다 몸 어디에 닿는지 — 제품을 모르는 방문자도 TactSleeve=팔 · TactVisor=얼굴을 알게(2026-10-26).
  // 부위는 **지원 기기에서** 정한다: 종전엔 포털 이벤트 이름에서 따로 뽑아 「Tactosy for Feet 지원 · 부위엔 발 없음」 같은 모순이 났다(VRChat).
  const zoneOf: Record<DeviceType, string> = {
    [DeviceType.Vest]: t("Body zone Torso"),
    [DeviceType.Head]: t("Body zone Face"),
    [DeviceType.Arm]: t("Body zone Arms"),
    [DeviceType.Hand]: t("Body zone Hands"),
    [DeviceType.Glove]: t("Body zone Hands"),
    [DeviceType.Foot]: t("Body zone Feet"),
  };
  // ®·™·© 는 문구 안에서 거슬려 뺀다 — 이름 자체는 그대로(정확성 영향 없음)
  const plainName = gameName
    .replace(/[®™©]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  const deviceChips = [
    ...new Map(
      devices.map((d) => [
        DeviceUtil.getFriendlyName(d),
        { name: DeviceUtil.getFriendlyName(d), zone: zoneOf[d] },
      ]),
    ).values(),
  ].filter((c) => c.name);

  return (
    <section ref={ref} data-testid="haptic-summary-card">
      <div className="rounded-2xl bg-gradient-to-br from-blue-60 via-[#4f46e5] to-[#7c3aed] p-[1.5px]">
        <div className="rounded-[15px] bg-white p-5 laptop:p-7">
          {summary ? (
            <>
              {/* 사람이 쓴 요약(source.type manual)은 AI 표시 대신 작성 주체를 단다 — AI 가 만든 문장이 아니다 */}
              {summary.byHuman ? (
                <div className="mb-2 text-xs text-coolgray-60">
                  {t("Written by the bHaptics team")}
                </div>
              ) : (
                <div className="mb-2 flex items-center gap-2 text-xs text-coolgray-60">
                  <span className="rounded border border-coolgray-80 px-1 py-px text-[11px] font-600 leading-none text-coolgray-90">
                    AI
                  </span>
                  <span>
                    {t(
                      "Summarized by AI from haptic events in the developer portal",
                    )}
                  </span>
                  <Tooltip
                    delayDuration={0}
                    align="center"
                    side="bottom"
                    collisionPadding={16}
                    // 공용 툴팁은 `break-all`(글자 단위 줄바꿈)이라 영어 단어가 중간에서 끊긴다 — 이 카드만 단어 단위로
                    className="max-w-[320px] break-normal [word-break:keep-all]"
                    tooltipContent={
                      <div className="whitespace-pre-line text-xs leading-relaxed">
                        {/* 2026-10-26 단순화 — 무엇을 했나 + 한계(실제 게임과 다를 수 있음 · 체감 개인차) */}
                        {t(
                          "AI summarized the haptic effects registered in the developer portal. It may differ from the actual game, and the feel varies from person to person.",
                        )}
                      </div>
                    }
                  >
                    <button
                      type="button"
                      aria-label={t("How this summary is made")}
                    >
                      <InfoIcon className="text-coolgray-50" />
                    </button>
                  </Tooltip>
                </div>
              )}

              {/* 제목 색은 기본값 — `laptop:text-coolgray-100` 은 데스크탑 시안이 #121619 로 확인된 섹션 넷만 쓴다(이 카드는 시안 없음 ·
              `game-detail-mobile-axis-parity.test.ts`) */}
              <h2 className="mb-5 text-2xl laptop:text-3xl">
                {t("What you'll feel in this game")}
              </h2>

              <ul className="flex flex-col gap-3">
                {summary.lines.map((line, i) => {
                  const style = KIND_STYLE[line.kind] ?? KIND_STYLE.world;
                  return (
                    <li
                      key={`${line.kind}-${i}`}
                      className={
                        !expanded && i >= MOBILE_LINES
                          ? "hidden laptop:block"
                          : undefined
                      }
                    >
                      <LineShell
                        categories={
                          onShowEffects
                            ? matchingEffectCategories(
                                line.kind,
                                effectCategories,
                              )
                            : []
                        }
                        onOpen={(categories) => {
                          analyticsInstance?.logHapticSummaryLineClick({
                            gameSlug: slug,
                            kind: line.kind,
                          });
                          onShowEffects?.(categories);
                        }}
                        label={t("See related haptic effects")}
                      >
                        <span
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg"
                          style={{ backgroundColor: `${style.color}1a` }}
                          aria-hidden
                        >
                          {style.icon}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <span
                            className="text-xs font-600"
                            style={{ color: style.color }}
                          >
                            {kindLabel[line.kind] ?? kindLabel.world}
                          </span>
                          <p className="text-base text-coolgray-80">
                            {line.text}
                          </p>
                        </div>
                      </LineShell>
                    </li>
                  );
                })}
              </ul>
              {summary.lines.length > MOBILE_LINES && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  aria-expanded={expanded}
                  className="mt-3 text-sm font-600 text-coolgray-70 underline laptop:hidden"
                >
                  {expanded ? t("Show less") : t("Show more")}
                </button>
              )}
            </>
          ) : support ? (
            <>
              <h2 className="mb-3 text-2xl laptop:text-3xl">
                {t("Play it with bHaptics")}
              </h2>
              <p className="text-base text-coolgray-80">
                {t(SUPPORT_LINE[support.type])}
              </p>
            </>
          ) : null}

          {deviceChips.length > 0 && (
            <div className="mt-5 flex flex-col gap-3 rounded-xl bg-blue-60/5 p-4 laptop:flex-row laptop:items-center laptop:justify-between">
              <div className="flex flex-col gap-2">
                {/* 제안 문구 — 지원 기기·「지원 기기 보기」 바로 위라 「이 게임을 위해 기기를」로 잇는다. 게임 이름을 넣어 지금 보는
                    게임에 대한 제안으로(2026-10-26 결정 — 종전 공통 문구 「한 번 느끼면, 없이는 허전해집니다」 대체) */}
                <p className="text-base font-600 text-coolgray-90">
                  {t("Don't just play {{game}}. Feel it.", { game: plainName })}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-600 text-coolgray-90">
                    {t("Feel it with")}
                  </span>
                  {deviceChips.map(({ name, zone }) => (
                    <span
                      key={name}
                      className="rounded-md bg-white px-2 py-0.5 text-coolgray-80"
                    >
                      {name}
                      {zone && (
                        <span className="text-coolgray-50"> · {zone}</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  analyticsInstance?.logHapticSummaryDeviceClick({
                    gameSlug: slug,
                  });
                  document
                    .getElementById("supported-devices")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="shrink-0 self-start rounded-full bg-blue-60 px-4 py-2 text-sm font-600 text-white laptop:self-auto"
              >
                {t("See supported devices")}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * 줄 한 칸 — 맞는 이펙트 카테고리가 있으면 눌러서 그 근거(이펙트 「전체 보기」)로 간다, 없으면 그냥 상자.
 * 요약이 원본을 가리지 않고 근거로 이어지게(아마존 「Customers say」 칩 → 원문).
 */
function LineShell({
  categories,
  onOpen,
  label,
  children,
}: {
  categories: string[];
  onOpen: (categories: string[]) => void;
  label: string;
  children: ReactNode;
}) {
  const base =
    "flex w-full items-start gap-3 rounded-xl bg-coolgray-10 p-3 text-left laptop:p-4";
  if (categories.length === 0) return <div className={base}>{children}</div>;
  return (
    // ★`aria-label` 을 달지 않는다 — 버튼 이름이 내용을 덮어 스크린리더가 요약 문장을 못 읽는다(#6083 R1).
    // 이름 = 줄 내용(종류 + 문장) + 숨긴 꼬리말 「관련 햅틱 이펙트 보기」
    <button
      type="button"
      onClick={() => onOpen(categories)}
      title={label}
      className={`${base} transition-colors hover:bg-coolgray-20`}
    >
      {children}
      <span className="sr-only">{label}</span>
      <ChevronRightIcon
        aria-hidden
        className="ml-auto h-5 w-5 shrink-0 self-center text-coolgray-50"
      />
    </button>
  );
}
